import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { num, apiError } from '../utils/format';
import { packsOf } from '../utils/packs';

// Reorder (min/max) levels per location & pack — thresholds live on the product.
// Used both as a tab on the product detail page and in a modal from the Products list.
const ReorderLevels = ({ product, canWrite }) => {
  const [locations, setLocations] = useState([]);
  const [rows, setRows] = useState([]);          // existing thresholds for this product
  const [draft, setDraft] = useState({ locationId: '', variantId: 0, minQty: '', maxQty: '' });
  const [loading, setLoading] = useState(true);

  const variants = product.variants || [];
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/thresholds?itemType=product&itemId=${product.id}`),
      api.get('/locations'),
    ]).then(([t, l]) => { setRows(t.data.thresholds || []); setLocations(l.data.locations || []); })
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [product.id]);
  useEffect(load, [load]);

  const locName = Object.fromEntries(locations.map((l) => [l.id, l.name]));
  const varName = { 0: 'Whole product', ...Object.fromEntries(variants.map((v) => [v.id, v.weightLabel])) };

  const save = async (locationId, variantId, minQty, maxQty) => {
    try {
      await api.post('/thresholds', { locationId: Number(locationId), itemType: 'product', itemId: product.id, variantId: Number(variantId) || 0, minQty: Number(minQty) || 0, maxQty: Number(maxQty) || 0 });
      toast.success('Reorder level saved'); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const addDraft = () => {
    if (!draft.locationId) return toast.error('Pick a location');
    save(draft.locationId, draft.variantId, draft.minQty, draft.maxQty);
    setDraft({ locationId: '', variantId: 0, minQty: '', maxQty: '' });
  };

  if (loading) return <div className="spinner" />;
  return (
    <div className="table-wrap">
      <table className="grid">
        <thead><tr><th>Location</th><th>Pack</th><th className="right">Min qty</th><th className="right">Max qty (0 = none)</th>{canWrite && <th></th>}</tr></thead>
        <tbody>
          {rows.map((t) => <ThresholdRow key={t.id} t={t} locName={locName} varName={varName} canWrite={canWrite} onSave={save} />)}
          {canWrite && (
            <tr>
              <td>
                <select value={draft.locationId} onChange={(e) => setDraft((d) => ({ ...d, locationId: e.target.value }))}>
                  <option value="">— location —</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </td>
              <td>
                <select value={draft.variantId} onChange={(e) => setDraft((d) => ({ ...d, variantId: e.target.value }))}>
                  <option value={0}>Whole product</option>
                  {packsOf(product).map((v) => <option key={v.id} value={v.id}>{v.weightLabel}</option>)}
                </select>
              </td>
              <td className="right"><input type="number" step="0.001" style={{ width: 90 }} value={draft.minQty} onChange={(e) => setDraft((d) => ({ ...d, minQty: e.target.value }))} /></td>
              <td className="right"><input type="number" step="0.001" style={{ width: 90 }} value={draft.maxQty} onChange={(e) => setDraft((d) => ({ ...d, maxQty: e.target.value }))} /></td>
              <td><button className="btn btn-primary btn-sm" onClick={addDraft}>Add</button></td>
            </tr>
          )}
          {!rows.length && !canWrite && <tr><td colSpan={4} className="empty">No reorder levels set.</td></tr>}
        </tbody>
      </table>
      <p className="muted small mt">Items below their minimum (or above maximum) at any location show up as alerts on the Dashboard.</p>
    </div>
  );
};

const ThresholdRow = ({ t, locName, varName, canWrite, onSave }) => {
  const [min, setMin] = useState(Number(t.minQty));
  const [max, setMax] = useState(Number(t.maxQty));
  return (
    <tr>
      <td>{locName[t.locationId] || `#${t.locationId}`}</td>
      <td>{varName[t.variantId] || `#${t.variantId}`}</td>
      <td className="right">{canWrite ? <input type="number" step="0.001" style={{ width: 90 }} value={min} onChange={(e) => setMin(e.target.value)} /> : num(t.minQty)}</td>
      <td className="right">{canWrite ? <input type="number" step="0.001" style={{ width: 90 }} value={max} onChange={(e) => setMax(e.target.value)} /> : (Number(t.maxQty) || '—')}</td>
      {canWrite && <td><button className="btn btn-outline btn-sm" onClick={() => onSave(t.locationId, t.variantId, min, max)}>Save</button></td>}
    </tr>
  );
};

export default ReorderLevels;
